import { notify } from "@kyvg/vue3-notification"
import type { ImageSet } from "./common"
import { $dy__fetchWithToken, extractDataOrFailure, useDYFetch, useDYFetchWithToken } from "./fetch"
import { useDYAppConfig } from "./config"
import { isNumber } from "lodash-es"
import { uniqueId } from "../libs/utils"
import type { EdgeCacheParams } from "./edge-cache"

export interface DYUploadedFileItem {
  no: number,
  /**
   * 파일의 실제 이름.
   */
  realName: string
}

export interface DYUploadedFileGroup {
  no: number,
  items?: DYUploadedFileItem[]
}

/**
 * 폼으로 넘길때의 기본형태
 */
export interface DYFileFormData {
  fieldName: string
  fileTypedPk: string|number
}

export interface DYFileItem {
  dimensions: { width: number, height: number }
  downAuth: string // self
  downAuthValue: string | null
  ext: string | "jpg"
  /**
   * 도메인 포함 fullUrl
   */
  fileUrl: string
  /**
   * ex) "2.jpg"
   */
  name: string
  no: number
  rank: number
  realName: string
  /**
   * 미메타입을 분석한 것을 바탕으로 한 파일타입
   */
  simpleType: "image" | 'video' | 'audio' | 'text' | 'application' | 'undefined'
  size: number
  srcSet: ImageSet
  /**
   * 미메타입
   */
  type: string
  /**
   * upFolder부터 시작하는 url
   */
  url: string
}

/**
 * uppy에서 업로드로 이벤트 넘길 때 넘어가는 데이터들
 */
export interface SendUploadFiles {
  /**
   * 현재 페이지가 전송완료 되기 까지 업로드한 모든 파일의 url 배열
   */
  url: string[],
  /**
   * 이번타이밍에 올린 모든 파일의 url 배열(업로더 모달에 파일들을 업로드 전송 완료 하기까지). 보통은 이걸로 컨트롤할듯?
   */
  currentFileUrls: string[],
  fileTypedPk: string|number,
  fileFormData: DYFileFormData
}

export interface ListResult<T> {
  items: T[]
  totalCount: number
  maxRank: number
}

/**
 * 에디터 본문 이미지를 올릴 자리. 툴바 업로더(CommonDYUploader)에 넘기는 값과 같게 줄 것 —
 * 같은 묶음(uniqueId)으로 올라가야 부모 폼이 한 번에 글과 연결한다(다르면 마지막 묶음만 연결된다, 2026-10-17)
 */
export interface DYEditorImageUploadTarget {
  tableName: string
  fieldName: string
  /** 에디터 화면당 고정한 묶음 키 — 툴바 업로더의 unique-id 와 같은 값 */
  uniqueId: number
  subjectNo?: number
  dbDomain?: string
}

export function useDYFileUploader() {
  const { API_BASE_URL } = useDYAppConfig()
  /**
   * 랭크 체인지
   * @returns
   */
  const changeRank = async (items: { no: number, rank: number }[]) => {
    return await $dy__fetchWithToken<{ message: string }>(API_BASE_URL+"/api/file-items/change-rank", { method: 'patch', body: { items } })
  }

  /**
   * 파일 아이템 넘버를 넘겨 파일 하나를 삭제하는 api
   * @param uploadedFileItemNo 
   */
  const deleteOne = async (uploadedFileItemNo: number) => {
    return await $dy__fetchWithToken<{ message: string, allDelete: boolean }>(API_BASE_URL+`/api/file-items/${uploadedFileItemNo}`, { method: 'delete' })
  }

  /**
   * 파일 그룹 넘버를 넘겨 해당 모든 파일들을 삭제하는 api
   * @param uploadedFileGroupNo 
   * @returns 
   */
  const deleteGroup = async (fileTypedPk: string) => {
    return await $dy__fetchWithToken<{ message: string }>(API_BASE_URL+`/api/file-items/group/${fileTypedPk}`, { method: 'delete' })
  }

  /**
   * 주어진 typedPk에 해당하는 파일 아이템 목록을ㅇ 가져옴
   * @param fileTypedPk 
   */
  const getUploadedFiles = async (fileTypedPk: string | number, params?: EdgeCacheParams) => {
    const parsed = isNumber(fileTypedPk) ? fileTypedPk : fileTypedPk.replace(/fg|fi/gi, '')
    if (parsed) {
      // params(refresh 등)를 쿼리에 병합 — 쓰기(업로드/삭제) 직후 호출에서 refresh:'y' 로 CF 엣지 바이패스.
      const response = await useDYFetch<ListResult<DYFileItem>>(API_BASE_URL+`/api/file-items/for-uploader`, { params: { relatedGroupNo: parsed, ...params }, immediate: false })
      await response.execute({dedupe: 'defer'})
      const data = extractDataOrFailure(response)
      return data
    }
    return undefined
  }

  /**
   * 에디터에 붙여넣은 이미지 파일 하나를 툴바 업로더와 같은 API·묶음으로 올린다. 2026-10-20
   * @returns 툴바 업로더의 upload-complete 와 같은 모양 — 그대로 부모의 upload:file 처리에 넘기면 된다
   */
  const uploadEditorImage = async (file: Blob, fileName: string, target: DYEditorImageUploadTarget): Promise<SendUploadFiles> => {
    const formData = new FormData()
    formData.append('tableName', target.tableName)
    formData.append('fieldName', target.fieldName)
    formData.append('uniqueId', String(target.uniqueId))
    formData.append('rank', '1')
    if (target.dbDomain) formData.append('dbDomain', target.dbDomain)
    if (target.subjectNo) formData.append('subjectNo', String(target.subjectNo))
    formData.append('file', file, fileName)
    const res = await $dy__fetchWithToken<{ groupNo: number, fileUrl: string }>(API_BASE_URL + '/api/file-uploads', { method: 'post', body: formData })
    if (!res?.fileUrl || !res.groupNo) throw new Error('이미지를 올리지 못했습니다')
    const fileTypedPk = 'fg' + res.groupNo
    return { url: [res.fileUrl], currentFileUrls: [res.fileUrl], fileTypedPk, fileFormData: { fieldName: target.fieldName, fileTypedPk } }
  }

  return {
    getUploadedFiles, changeRank, deleteOne, deleteGroup, uploadEditorImage
  }
}

export function useDYLegacyFileDownloader() {
  const { fullDomain, API_BASE_URL } = useDYAppConfig()

  const downAuth = ref({
    every: 'every',
    activePayment: 'activePayment',
    adminOnly: 'adminOnly',
    self: 'self',
  })

  /**
   * @param fileTypedPk // 압축다운할 핵심적인 파일. fg없이 숫자만 있어도 괜찮음
   * @param typedPk     // 특정 용도의 압축파일만 별도 처리하기 위한 구분값
   * @param dbDomain 
   * @param overwrite   // 기존 파일 덮어씌워 매번 새로 압축할건지
   * @param rankOrder   // 랭크순대로 파일명을 재정렬해서 리턴할건지
   */
  const makeZipAndDownload = async (fileTypedPk: string|number, typedPk: string, dbDomain: string, overwrite: boolean = false, rankOrder: boolean = false) => {
    const response = await useDYFetch(
      `/doyac/rest/file/makeZip.php`,
      {
        query: {
          fileTypedPk: `${fileTypedPk}`,
          typedPk: `${typedPk}`,
          linkDb: `${dbDomain}`,
          overwrite: overwrite,
          rankOrder: rankOrder
        },
        immediate: false
      }
    )
    await response.execute({ dedupe: 'defer' })

    location.href = '/doyacimpl/downloadFileImpl.php'
  }

  const getUserKey = async () => {
    if (!import.meta.client) {
      return undefined
    }

    const userKey = useCookie('userKey')
    if (!userKey.value) {
      const response = await useDYFetchWithToken<any>(API_BASE_URL+`/api/user-key`, { immediate: false })
      await response.execute({dedupe: 'defer'})
      const data = extractDataOrFailure(response)
      if (data.success && data.value) {
        userKey.value = data.value
      }
    }
    return userKey.value
  }

  /**
     * @param itemNo //파일 아이템 넘버
     * @param downAuth //다운어스 권한은 보통 파일데이터안의 auth값을 그대로 주면 되나 특정 권한을 주고 싶을 경우 따로 입려가면 된다.
     *  ex) every(아무나 다운 가능. 비회원 포함) / activePayment(실제 결제자와 관리자만. downAuthValue 값으로 typedPk를 보내야 함 ) // adminOnly(관리자만) // self(본인과 관리자만) 등등.
     *  새로운 조건 추가를 원할 경우 PHP의 UploadedFileItem에서 권한 상수값 추가하고 FileDownloader의 권한체크 로직에서 해당 코드 추가할 것!
     * @param downAuthValue // 일부 권한 체크시 보내야 하는 밸류 값
     * @returns {Promise<void>}
     * 기본적으로 plupload방식을 사용한 다운로드 파일 지원 메서드
     */
  const download = async (itemNo: number, downAuth: string, downAuthValue?: string) => {
    const userKey = await getUserKey()

    if (userKey) {
      const params = new URLSearchParams()
      params.append('userKey', userKey)
      params.append('itemNo', `${itemNo}`)
      params.append('downAuth', downAuth)
      if (downAuthValue) params.append('downAuthValue', downAuthValue)
      params.append('noEncrypt', 'true') //넉스트에선 SSL방식이므로 이제 인크립트할 필요가 없음

      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      }

      try {
        const raw = await $dy__fetchWithToken<any>(
          `${location.origin}/doyac/rest/accessKey/issueKey.php`,
          { ...config, method: 'post' }
        )

        // 구방식 API이기 때문에 응답 Content-Type이 text/html로 전달된다. 따라서 직접 JSON 파싱을 해야 함. 그러나 extractDataOrFailure를 태우고 나면 알아서 되므로 여기서 한번더 하면 안됨!!
        location.href = `/doyac/downloadFile.php?accessKeyForNewDown=${raw.accessKey}`
      } catch (e) {
        notify({ type: 'error', text: '다운로드 오류!!!' })
      }
    } else {

    }
  }

  /**
   * 완전 구방식의 파일 다운로드. 이건 왠만하면 안 써야함
   * @param fileName 
   * @param realName 
   * @param linkDb 
   * @param boardSkin 
   * @param boardEntryNo 
   */
  const oldDownloadFile = async (fileName: string, realName: string, linkDb?: string, boardSkin?: string, boardEntryNo?: number) => { //파일네임과 리얼네임을 넘겨 다운받게 하는 구방식의 다운로드. 가능한 쓰지 말것
    if (!linkDb) linkDb = ''
    if (!boardSkin) boardSkin = ''

    const noQuery = boardEntryNo ? `no=${boardEntryNo}&` : 'no=&'

    try {
      location.href = `/doyac/downloadFile.php?boardName=${boardSkin}&${noQuery}file=${fileName}&rfile=${realName}&linked=${linkDb}`
    } catch (e) {
      notify({ type: 'error', text: '다운로드 오류!!' })
    }
  }

  return {
    downAuth, download, oldDownloadFile, makeZipAndDownload
  }
}